import React, { useState, useRef, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Header from '../../components/Header';
import { api, openUploadDownload } from '../../api/client';
import { useOfflineQuery } from '../../lib/offline/use-offline-query';
import { useSyncStore } from '../../lib/offline/sync-store';
import { calendarDayString, formatCalendarDate, formatInstantDay, formatScheduledTime } from '../../lib/calendar-date';
import { useTenantTimeZone } from '../../hooks/useTenantTimeZone';
import { jobStatusLabel, jobStatusStyle } from '../../lib/job-status';
import { crewRoleLabel } from '../../lib/display-labels';
import { useAuth } from '../../contexts/AuthContext';
import JobChatDrawer from '../../components/JobChatDrawer';
import FieldCasingStringCard from '../../components/casing/FieldCasingStringCard';
import type { JobCasingString } from '../../types/casing-string';
import { getJobTypeLabel } from '../../constants/job-types';
import ConfirmModal from '../../components/ui/ConfirmModal';
import JobTicketsCard from '../../components/field/JobTicketsCard';
import WellInfoFillCard from '../../components/field/WellInfoFillCard';
// No CertWarningList here on purpose: the endpoint computes `warnings` on the
// ADD ids only, so a remove-only call always returns an empty array. Getting
// out-of-cert gear OFF a job is the outcome the warning exists to encourage —
// it is never itself warned about, and never 409s.
import { toast } from 'sonner';
import {
  ArrowLeft,
  MapPin,
  Calendar,
  Clock,
  Users,
  Wrench,
  Plus,
  Trash2,
  CheckSquare,
  Square,
  Ticket,
  Phone,
  ClipboardCheck,
  Navigation,
  Shield,
  Loader2,
  MessageCircle,
  Paperclip,
  Upload,
  Download,
  FileText,
  AlertTriangle,
  Link2,
  X,
  Play,
  Pause, CheckCircle2, ReceiptText, CircleStop, RotateCw } from 'lucide-react';
import { useBack } from '../../hooks/useBack';
import LoadFailureNotice from '../../components/ui/LoadFailureNotice';
import { LOAD_FAILURE_QUERY_OPTIONS, isRefusal, queryFailureError, queryLoadFailure } from '../../lib/load-failure';
import { Tooltip } from '../../components/ui/tooltip';
import {
  PHASE_WORD,
  handPhases,
  normalizeMyPhase,
  phaseDateValue,
  phaseMismatch,
  shownPhases,
  type FieldPhase,
} from '../../lib/job-phase-dates';

const ALLOWED_ATTACHMENT_TYPES = '.pdf,.jpg,.jpeg,.png,.csv,.doc,.docx,.xls,.xlsx';
const ALLOWED_MIME_SET = new Set([
  'application/pdf',
  'image/jpeg',
  'image/png',
  'text/csv',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
]);
const ALLOWED_EXT_SET = new Set(['pdf', 'jpg', 'jpeg', 'png', 'csv', 'doc', 'docx', 'xls', 'xlsx']);
const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50 MB

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Turn a failed start/hold into something a hand in a truck can act on.
 *
 * 404 IS NOT "MISSING". The endpoints are crew-gated and answer a non-crew
 * worker with 404 rather than 403, deliberately, so job ids cannot be probed by
 * reading status codes. That makes 404 unambiguous here — the job plainly
 * exists, we are looking at it — and a generic "not found" would read as a bug
 * in the app rather than the access rule it actually is.
 *
 * It is reachable, not theoretical: GET /employee/my-jobs/:id is scoped to the
 * company only, so a worker who arrives at a job by direct link sees the whole
 * screen and these buttons, and finds out they are not on the crew by pressing
 * one.
 *
 * 409 CARRIES THE API'S OWN SENTENCE and it is better than anything written
 * here, because it names the current status ("This job is completed. Reopening
 * it is an office action — ask dispatch."). Surface it verbatim.
 */
function statusErrorMessage(err: any): string {
  const httpStatus = err?.response?.status;
  if (httpStatus === 404) {
    return "You're not on this job's crew, so you can't change its status. Ask dispatch to add you to the crew.";
  }
  return (
    err?.response?.data?.error ||
    err?.message ||
    'Could not change the job status. Try again.'
  );
}

function validateAttachmentFile(file: File): string | null {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!ALLOWED_EXT_SET.has(ext) && !ALLOWED_MIME_SET.has(file.type)) {
    return `File type ".${ext}" is not allowed. Allowed: PDF, JPEG, PNG, CSV, Word, Excel.`;
  }
  if (file.size > MAX_FILE_SIZE) {
    return `File is too large (${formatFileSize(file.size)}). Maximum size is 50 MB.`;
  }
  return null;
}

// FieldPhase — the phase a field Start or End sends. Never 'both': wash and
// run are always started and ended separately, even on a same-day job — the
// wash crew usually goes out first and finishes first (Jason, 2026-10-11;
// api#1028). The API still accepts 'both' from old builds and stamps the wash
// only. Defined in lib/job-phase-dates.ts with the rule for which phases a
// hand is shown.


export default function FieldJobDetail() {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();
  const goBack = useBack('/field/jobs');
  // An attachment's upload instant is shown as the TENANT's day.
  const timeZone = useTenantTimeZone();
  // Which of the job's day-rows the hand came from on the field dashboard —
  // that row's `day_phase` chip, handed over in route state. A 'wash' or 'run'
  // row narrows the phase buttons to that day's phase. A 'both' row (a
  // same-day job) and no row at all narrow nothing: every button is a phase of
  // its own, so the tap itself says which clock it is.
  const routeState = useLocation().state as { dayPhase?: unknown } | null;
  const routeDayPhase: FieldPhase | undefined =
    routeState?.dayPhase === 'wash' || routeState?.dayPhase === 'run' ? routeState.dayPhase : undefined;
  const { user } = useAuth();
  const [certLoading, setCertLoading] = useState<string | null>(null);
  // A job-chat push opens this screen with ?chat=1 (fieldiq-api job-chat-notify).
  const [chatOpen, setChatOpen] = useState(() => new URLSearchParams(window.location.search).get('chat') === '1');
  const [message, setMessage] = useState<{ type: string; text: string } | null>(null);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [attachmentError, setAttachmentError] = useState<string | null>(null);
  const [showAddLink, setShowAddLink] = useState(false);
  const [linkLabel, setLinkLabel] = useState('');
  const [linkUrl, setLinkUrl] = useState('');
  const [linkError, setLinkError] = useState<string | null>(null);
  const [statusError, setStatusError] = useState<string | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const isOnline = useSyncStore((s) => s.isOnline);

  const toggleTaskMutation = useMutation({
    mutationFn: ({ taskId, isCompleted }: { taskId: string; isCompleted: boolean }) =>
      api.dispatch.updateTask(jobId!, taskId, { isCompleted }),
    onMutate: async ({ taskId, isCompleted }) => {
      await queryClient.cancelQueries({ queryKey: ['field-job-tasks', jobId] });
      const previous = queryClient.getQueryData(['field-job-tasks', jobId]);
      // eslint-disable-next-line no-restricted-syntax -- TODO PR 2: migrate to setQueryDataMapped (see docs/audits/setquerydata-audit.md)
      queryClient.setQueryData(['field-job-tasks', jobId], (old: any) =>
        old ? (old as any[]).map((t: any) => t.id === taskId ? { ...t, isCompleted, is_completed: isCompleted } : t) : []
      );
      return { previous };
    },
    onError: (_err: any, _vars, context) => {
      // eslint-disable-next-line no-restricted-syntax -- TODO PR 2: migrate to setQueryDataMapped (see docs/audits/setquerydata-audit.md)
      if (context?.previous) queryClient.setQueryData(['field-job-tasks', jobId], context.previous);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['field-job-tasks', jobId] });
      queryClient.invalidateQueries({ queryKey: ['field-job', jobId] });
    },
  });

  /**
   * Every query in the field tree that renders a job's status.
   *
   * ['field-my-jobs'] IS A PREFIX, NOT A KEY. FieldJobsScreen keys on
   * ['field-my-jobs', activeTab] — one cached list per status tab — so an exact
   * match would refresh whichever tab the worker last opened and leave the
   * other four stale. React Query treats a shorter key as a prefix, so this
   * invalidates all of them.
   *
   * ['field-dashboard'] is FieldHomeScreen's today/upcoming lists, which show
   * the same status pill. Missing it means a worker starts a job, taps Home,
   * and reads "scheduled" for the job they are standing on.
   */
  const invalidateJobStatus = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['field-job', jobId] });
    queryClient.invalidateQueries({ queryKey: ['field-my-jobs'] });
    queryClient.invalidateQueries({ queryKey: ['field-dashboard'] });
  }, [queryClient, jobId]);

  /**
   * Start, resume and hold.
   *
   * NO OPTIMISTIC UPDATE, unlike the task toggle above. A checkbox that flickers
   * back is a nuisance; a status that reads "Active" for two seconds and then
   * silently reverts is a crew believing the office can see them working. The
   * server is the only authority on this field, so the button spins until it
   * answers.
   *
   * A 409 means the client was looking at a stale status — the office moved the
   * job while the phone was showing the old one. Refetch on top of showing the
   * message, so the buttons redraw to match what is actually true.
   *
   * `changed` and `equipment_acquired` in the response are ignored on purpose.
   * The first is idempotency bookkeeping and the second is the equipment
   * cascade's business; neither is something to report to a field worker.
   */
  const startJobMutation = useMutation({
    // The phase is the BUTTON's, never inferred. `undefined` sends none, which
    // stamps no phase clock — only the fallback Start Job / Resume with no
    // phase to name use it.
    mutationFn: (phase: FieldPhase | undefined) => api.employee.startJob(jobId!, phase),
    onSuccess: () => {
      setStatusError(null);
      invalidateJobStatus();
    },
    onError: (err: any) => {
      setStatusError(statusErrorMessage(err));
      if (err?.response?.status === 409) invalidateJobStatus();
    },
  });

  /**
   * End one phase (api#1031). Crew-gated like Start. The API's 409 sentence
   * ("has not been started", "has already been ended") means this screen was
   * stale, so it is shown and the job refetched.
   */
  const endPhaseMutation = useMutation({
    mutationFn: (phase: FieldPhase) => api.employee.endPhase(jobId!, phase),
    onSuccess: () => {
      setStatusError(null);
      invalidateJobStatus();
    },
    onError: (err: any) => {
      setStatusError(statusErrorMessage(err));
      if (err?.response?.status === 409) invalidateJobStatus();
    },
  });

  /**
   * The crew is back on an ENDED phase (API 0534) — opens a new stint now.
   * The 409 sentence (closed job, never started, still running) is shown.
   */
  const resumePhaseMutation = useMutation({
    mutationFn: (phase: FieldPhase) => api.employee.resumePhase(jobId!, phase),
    onSuccess: () => setStatusError(null),
    onError: (err: any) => setStatusError(statusErrorMessage(err)),
    onSettled: () => {
      invalidateJobStatus();
      queryClient.invalidateQueries({ queryKey: ['dispatch-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['job', jobId] });
    },
  });

  const stopJobMutation = useMutation({
    mutationFn: () => api.employee.stopJob(jobId!),
    onSuccess: () => {
      setStatusError(null);
      invalidateJobStatus();
    },
    onError: (err: any) => {
      setStatusError(statusErrorMessage(err));
      if (err?.response?.status === 409) invalidateJobStatus();
    },
  });

  /**
   * A hand declares the work done. Not a status change — see the card comment.
   *
   * `changed: false` is not surfaced. It means somebody already declared it
   * (possibly this hand, double-tapping on a cold morning), and the screen
   * redraws to the finished state either way, which is the honest answer to
   * "did my tap count?". Reporting "no change" would read as a failure.
   */
  const workFinishedMutation = useMutation({
    mutationFn: () => api.employee.markWorkFinished(jobId!),
    onSuccess: () => {
      setStatusError(null);
      invalidateJobStatus();
    },
    onError: (err: any) => {
      setStatusError(statusErrorMessage(err));
      if (err?.response?.status === 409) invalidateJobStatus();
    },
  });

  /**
   * Withdrawing the declaration. Same error handling as its sibling — the API's
   * own 409 sentence names what happened ("The office has already closed this
   * job (completed)...") and is better than anything written here.
   */
  const undoWorkFinishedMutation = useMutation({
    mutationFn: () => api.employee.unmarkWorkFinished(jobId!),
    onSuccess: () => {
      setStatusError(null);
      invalidateJobStatus();
    },
    onError: (err: any) => {
      setStatusError(statusErrorMessage(err));
      if (err?.response?.status === 409) invalidateJobStatus();
    },
  });

  const handleViewCert = async (equipmentId: string) => {
    setCertLoading(equipmentId);
    try {
      const res = await api.employee.getEquipmentCertUrl(equipmentId);
      const uploadId = res.data.data?.uploadId;
      if (uploadId) {
        void openUploadDownload(uploadId);
      }
    } catch (err) {
      console.error('Failed to get cert URL:', err);
    } finally {
      setCertLoading(null);
    }
  };

  /*
   * ─── Removing a piece of equipment ──────────────────────────────────────
   *
   * ANYONE REMOVES ANYTHING. There is no ownership model: `assigned_source` is
   * provenance, not a claim, and the endpoint never consults it to decide
   * whether a write is allowed. A hand who did not add the spider can still
   * take it off the job — which is the whole point, because the person at the
   * shop at 05:00 is not necessarily the person who typed it in.
   *
   * Removes are never cert-checked and can never 409: getting out-of-cert gear
   * OFF a job is the outcome the warning exists to encourage.
   */
  const [removeTarget, setRemoveTarget] = useState<any | null>(null);

  const removeEquipment = useMutation({
    mutationFn: (equipmentId: string) =>
      api.employee.assignJobEquipment(jobId!, { remove: [equipmentId] }),
    onSuccess: () => {
      setRemoveTarget(null);
      queryClient.invalidateQueries({ queryKey: ['field-job', jobId] });
      queryClient.invalidateQueries({ queryKey: ['field-available-equipment', jobId] });
      queryClient.invalidateQueries({ queryKey: ['available-equipment', jobId] });
      queryClient.invalidateQueries({ queryKey: ['field-my-equipment'] });
      queryClient.invalidateQueries({ queryKey: ['equipment'] });
      toast.success('Equipment removed');
    },
    onError: (err: any) => {
      const status = err?.response?.status;
      const body = err?.response?.data;
      setRemoveTarget(null);
      if (status === 403) { toast.error(body?.error || 'Field equipment assignment is not enabled for this company.'); return; }
      if (status === 404) { toast.error('You are not on the crew for this job.'); return; }
      toast.error(body?.error || 'Could not remove equipment');
    },
  });

  /*
   * THREE WAYS NOT TO HAVE A JOB, AND THEY ARE NOT ONE STATE.
   *
   * This screen is where the ticket-reminder SMS lands (api 0441). It used to
   * destructure `{ data, isLoading }` only, so a 404, a 403, a 500 and a dead
   * zone all rendered "Job not found" — which is what a hand opening the text
   * on their phone was shown, about a job that exists.
   *
   * 404 here is the crew gate, not a missing job: GET /employee/my-jobs/:id
   * answers a field worker who is not on the crew with 404 so ids cannot be
   * probed (lib/job-access.ts, enforceJobCrewMembership). It is an answer, so
   * it is not retried, and it gets its own sentence naming the remedy. Every
   * other failure goes to the shared notice.
   */
  const jobQuery = useQuery({
    queryKey: ['field-job', jobId],
    queryFn: () => api.employee.myJob(jobId!).then(r => r.data.data ?? r.data),
    enabled: !!jobId,
    ...LOAD_FAILURE_QUERY_OPTIONS,
    retry: (failureCount: number, error: unknown) =>
      (error as any)?.response?.status !== 404 && !isRefusal(error) && failureCount < 2,
  });
  const { data: jobData, isLoading } = jobQuery;
  const jobFailure = queryFailureError(jobQuery);
  const jobFailureKind = queryLoadFailure(jobQuery);
  const notOnCrew = (jobFailure as any)?.response?.status === 404;
  const job = jobData as any;

  // Fetch tasks separately (the embedded SQL subquery may return empty)
  const { data: tasksData, isFetched: tasksFetched } = useQuery({
    queryKey: ['field-job-tasks', jobId],
    queryFn: () => api.dispatch.getTasks(jobId!).then(r => r.data.data ?? r.data ?? []),
    enabled: !!jobId,
  });

  // Attachments
  const { data: attachments } = useQuery({
    queryKey: ['job-attachments', jobId],
    queryFn: () => api.jobs.listAttachments(jobId!).then(r => r.data.data ?? []),
    enabled: !!jobId,
  });

  /*
   * ─── The tickets already on this job ────────────────────────────────────
   *
   * SAME QUERY KEY, SAME FETCHER, SAME STORE as FieldTicketsScreen — deliberately
   * the shared ['field-my-tickets'] cache and not a job-scoped call of its own.
   * Two reasons, and the second one is the sharp edge:
   *
   *  1. GET /employee/my-tickets has no job filter to pass. Checked in the
   *     route's SQL, not assumed: it narrows only on `?status=`, it takes no
   *     date or job parameter, and its visibility clause admits any member of
   *     the job's crew. Every ticket on this job this hand may see is already in that one
   *     list, on any day — which is what makes a wash day and a run day on one
   *     ticket a question this screen can answer.
   *
   *  2. `useOfflineQuery` mirrors each successful fetch into IndexedDB by
   *     CLEARING the entity store and rewriting it from the response. A second,
   *     job-filtered query writing to the same `tickets` store would therefore
   *     evict every other job's tickets from the offline cache the moment this
   *     screen loaded. Fetch the whole list, filter in the client.
   *
   * Filtering here rather than through the hook's `filterFn` option for the same
   * reason: that option applies only to the IndexedDB read path, so using it
   * would filter offline and not online.
   */
  const { data: myTicketsData, isLoading: ticketsLoading } = useOfflineQuery(
    'tickets',
    ['field-my-tickets'],
    () => api.employee.myTickets().then(r => r.data.data ?? r.data ?? []),
    { enabled: !!jobId },
  );

  /*
   * Chronological, absent days last.
   *
   * The API orders this list by `created_at DESC`, which is right for "my
   * tickets" and wrong here: on a job the crew reads as a sequence of days, and
   * the wash day was not necessarily typed in first. Sorting is a display
   * choice, not a recomputation of anything the API reports.
   *
   * `work_date` is a calendar day ('YYYY-MM-DD'), so the strings sort correctly
   * as strings and no Date is constructed — see lib/calendar-date.ts for why
   * building one here would be the bug rather than the fix.
   */
  const jobTickets = useMemo(() => {
    const all = Array.isArray(myTicketsData) ? myTicketsData : [];
    const mine = all.filter((t: any) => (t?.job_id ?? t?.jobId) === jobId);
    return [...mine].sort((a: any, b: any) => {
      const ad = calendarDayString(a?.work_date) ?? '';
      const bd = calendarDayString(b?.work_date) ?? '';
      if (ad === bd) return 0;
      if (!ad) return 1;
      if (!bd) return -1;
      return ad < bd ? -1 : 1;
    });
  }, [myTicketsData, jobId]);

  /*
   * The second act the create button now requires.
   *
   * Held as state rather than done with window.confirm because the modal has to
   * NAME the tickets that already exist — "you already have T-1049 for Aug 30"
   * is the sentence that stops the duplicate, and a yes/no prompt is not.
   */
  const [confirmSecondTicket, setConfirmSecondTicket] = useState(false);

  const uploadAttachmentMutation = useMutation({
    mutationFn: (file: File) => api.jobs.uploadAttachment(jobId!, file, setUploadProgress),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-attachments', jobId] });
      setUploadProgress(null);
      setAttachmentError(null);
      setMessage({ type: 'success', text: 'File uploaded' });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err: any) => {
      setUploadProgress(null);
      const msg = err?.response?.data?.error || err?.message || 'Upload failed';
      setAttachmentError(msg);
    },
  });

  const handleAttachmentSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    if (!file) return;
    const error = validateAttachmentFile(file);
    if (error) {
      setAttachmentError(error);
      return;
    }
    setAttachmentError(null);
    uploadAttachmentMutation.mutate(file);
  }, [uploadAttachmentMutation]);

  const addLinkMutation = useMutation({
    mutationFn: (data: { label: string; url: string }) => api.jobs.addLinkAttachment(jobId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['job-attachments', jobId] });
      setShowAddLink(false);
      setLinkLabel('');
      setLinkUrl('');
      setLinkError(null);
      setMessage({ type: 'success', text: 'Link added' });
      setTimeout(() => setMessage(null), 3000);
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.error || err?.message || 'Failed to add link';
      setLinkError(msg);
    },
  });

  const handleAddLink = useCallback(() => {
    const trimmedLabel = linkLabel.trim();
    const trimmedUrl = linkUrl.trim();
    if (!trimmedLabel) {
      setLinkError('Label is required');
      return;
    }
    try {
      const parsed = new URL(trimmedUrl);
      if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
        setLinkError('URL must start with http:// or https://');
        return;
      }
    } catch {
      setLinkError('Please enter a valid URL (e.g. https://drive.google.com/...)');
      return;
    }
    setLinkError(null);
    addLinkMutation.mutate({ label: trimmedLabel, url: trimmedUrl });
  }, [linkLabel, linkUrl, addLinkMutation]);

  // The single job-level `useTorqueProfile` call that used to live here is
  // gone. It was keyed off the flat string-1 columns and its result was
  // rendered on the one and only casing card, so a hand running string 2 read
  // string 1's makeup torque window. Torque is now looked up per string, inside
  // FieldCasingStringCard — see that file for why a hook cannot be called in a
  // loop and a child component is the only legal shape.

  if (isLoading) {
    return (
      <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
        <div className="max-w-[1920px] mx-auto"><Header /></div>
        <div className="flex items-center justify-center py-20 text-fieldiq-text">Loading...</div>
      </div>
    );
  }

  if (!job) {
    return (
      <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-[var(--bottom-chrome)]">
        <div className="max-w-[1920px] mx-auto"><Header /></div>
        {notOnCrew ? (
          <div role="status" data-testid="field-job-not-on-crew" className="px-4 py-20 text-center">
            <p className="text-sm text-fieldiq-text">You’re not on this job’s crew</p>
            <p className="mx-auto mt-1 max-w-[46ch] text-xs text-fieldiq-muted">
              You can only open jobs you’re crewed on. Ask dispatch to add you, then open the link again.
            </p>
          </div>
        ) : jobFailureKind ? (
          <LoadFailureNotice error={jobFailure} kind={jobFailureKind} subject="the job" className="py-20" />
        ) : (
          <div className="text-center py-20 text-fieldiq-text">Job not found</div>
        )}
      </div>
    );
  }

  const crew = job.crew ?? [];
  const equipment = job.equipment ?? [];

  /*
   * ─── The field-equipment capability flag ────────────────────────────────
   *
   * `companies.field_crew_equipment_enabled`, off by default, and it rides on
   * THIS payload rather than on /auth/me — API #365's call, and the better one.
   * /auth/me is cached hard by React Query, so a tenant toggling the flag would
   * not see it until the token refreshed. This screen already fetches the job,
   * so the flag costs no round trip and cannot go stale relative to the job it
   * is about.
   *
   * DO NOT "improve" this onto /auth/me or into AuthContext. It is the same
   * column POST /my-jobs/:id/equipment gates on — one source read twice, not a
   * second copy. If the two ever disagree, the crew gets a button that 403s.
   *
   * snake_case because every other key on this endpoint is (serial_number,
   * unit_number, cert_upload_id); camelCase here would be the mismatch.
   */
  const canAssignEquipment = job.field_crew_equipment_enabled === true;
  const tasks = tasksFetched ? (tasksData ?? []) : (job.tasks ?? []);

  /*
   * ─── Casing strings ─────────────────────────────────────────────────────
   *
   * N strings, from `job_casing_strings` via GET /employee/my-jobs/:id. The
   * screen used to read the flat `casing_*` columns on the job row, which hold
   * two fixed sets and were rendered as one card.
   *
   * SORTED HERE AS WELL AS SERVER-SIDE. The endpoint already does ORDER BY
   * string_number and this is not a recomputation of a figure the API returned
   * — it is ordering, and it makes "ordered by string_number ascending" true by
   * construction rather than by trusting a clause in a file two repos away. The
   * fallback below has no ORDER BY at all, and neither will the offline cache
   * path when the client finally consumes the sync feed.
   *
   * FALLBACK — a job with ZERO child rows but a flat casing_size_inches renders
   * string 1 from the legacy columns. All 173 existing jobs were backfilled by
   * 0289 and every write path has dual-written since step 2, so this should
   * never fire. It exists because a card that renders nothing on a rig floor is
   * worse than one that renders the legacy value, and "should never fire" is
   * not the same as "cannot".
   */
  /*
   * A string whose six spec columns are ALL empty carries nothing a hand can
   * act on, and rendering it produces a card of four em dashes. The backfill
   * predicate in 0289 was deliberately loose — "any one of the six is not
   * null" — to keep incomplete office data rather than drop it, so rows like
   * Smith J-0017 (only `casing_weight_kg_m`) exist and are exactly this shape.
   * They render no casing card today and they render none now.
   *
   * The bar is ANY of the six, not any of the four the card displays: a string
   * carrying only a metric size is thin, but it is real data and the card says
   * so with dashes in the cells it cannot fill.
   */
  const hasAnySpec = (s: JobCasingString) =>
    !!(s.size_inches || s.size_mm || s.weight_lb_ft || s.weight_kg_m || s.grade || s.thread_type);

  const casingStrings: JobCasingString[] = (() => {
    const fromApi: JobCasingString[] = Array.isArray(job.casingStrings) ? job.casingStrings : [];
    if (fromApi.length > 0) {
      return [...fromApi]
        .filter(hasAnySpec)
        .sort((a, b) => Number(a.string_number) - Number(b.string_number));
    }
    if (job.casing_size_inches) {
      return [{
        string_number: 1,
        size_inches: job.casing_size_inches ?? null,
        size_mm: job.casing_size_mm ?? null,
        weight_lb_ft: job.casing_weight_lb_ft ?? null,
        weight_kg_m: job.casing_weight_kg_m ?? null,
        grade: job.casing_grade ?? null,
        thread_type: job.thread_type ?? null,
      }];
    }
    return [];
  })();

  /**
   * Which status buttons this job gets, mirroring what the API will actually
   * accept — start takes pending | scheduled | on_hold, stop takes active only.
   * A button that is certain to 409 is worse than no button.
   *
   * completed and cancelled get nothing. Reopening is an office action.
   *
   * The card also stays mounted while a statusError is showing, even when no
   * button qualifies. A 409 invalidates and refetches, so the status can change
   * out from under the buttons in the same beat — without this the explanation
   * for what just happened would unmount before it was read.
   */
  const closed = job.status === 'completed' || job.status === 'cancelled';
  const canHold = job.status === 'active';

  /**
   * ─── WASH AND RUN ARE STARTED AND ENDED SEPARATELY ──────────────────────
   *
   * Always, even on a same-day ("Wash + Run") job: the wash crew usually goes
   * out first and finishes first (Jason, 2026-10-11). One tap stamping both
   * clocks with one instant records a run start nobody saw — TNT J-4372, whose
   * run then sat on the start day after it was moved. So every Start and End
   * here names ONE phase, and nothing sends 'both'.
   *
   * WHICH PHASES THIS HAND GETS:
   *   1. the job's phases — the run always (scheduled_date plans it), the wash
   *      only when one is dated, so no "Start Wash" invites a clock nobody can
   *      explain later;
   *   2. narrowed to the hand's own crew phase (top-level `my_phase`, else
   *      crew[].phase) when it is 'wash' or 'run'. 'both', NULL, or a payload
   *      from before api#1031 means EVERY phase — the reading the call sheet
   *      and the board give it — so both show. If that narrowing leaves
   *      NOTHING (a wash hand on a job with no wash day) the hand gets no
   *      phase button and a line saying why. It used to fall back to the job's
   *      phases, which put the run's button on a wash hand's screen (TNT
   *      J-4404) — see lib/job-phase-dates.ts;
   *   3. START buttons, and the dates in Job Details, narrowed once more to
   *      the dashboard row's day when the hand came from a 'wash' or 'run' row
   *      they work. That row's day comes from SQL in
   *      the tenant's zone (api lib/day-phase.ts) and is not recomputed here —
   *      the browser's today is not the tenant's after 18:00 in Alberta. END
   *      buttons are not narrowed by day: a wash left running yesterday must be
   *      endable from the run day's row.
   *
   * A started phase loses its Start and gains an End; an ended one has
   * neither. No local flags — the mutations refetch ['field-job'], so a phase
   * started by another hand or by dispatch redraws here too.
   */
  const phaseStarted: Record<FieldPhase, boolean> = {
    wash: (job.wash_started_at ?? null) != null,
    run: (job.run_started_at ?? null) != null,
  };
  const phaseEnded: Record<FieldPhase, boolean> = {
    wash: (job.wash_ended_at ?? null) != null,
    run: (job.run_ended_at ?? null) != null,
  };
  // `my_phase` is the API's statement of this hand's phase (TNT J-4404); the
  // crew row's own phase is the same column, read when the key is absent.
  const myCrewPhase = normalizeMyPhase(
    job.my_phase !== undefined
      ? job.my_phase
      : crew.find((c: any) => c?.id != null && c.id === user?.id)?.phase,
  );
  // No fallback — a wash hand on a job with no wash day gets [] here.
  const myPhases: FieldPhase[] = handPhases(job, myCrewPhase);
  const mismatchedPhase = phaseMismatch(job, myCrewPhase);
  const startOffered: FieldPhase[] = shownPhases(job, myCrewPhase, routeDayPhase);
  const startablePhases: FieldPhase[] = closed ? [] : startOffered.filter((p) => !phaseStarted[p]);
  const endablePhases: FieldPhase[] = closed ? [] : myPhases.filter((p) => phaseStarted[p] && !phaseEnded[p]);
  // An ended phase can be resumed (API 0534) — the crew is back after a gap.
  const resumablePhases: FieldPhase[] = closed ? [] : myPhases.filter((p) => phaseEnded[p]);

  // Start Job with NO phase only when there is no phase left to name — a
  // pending job whose offered phases are all already stamped (J-4372's shape).
  // It moves the status and stamps no clock.
  // Never on a mismatched hand: with no phase of theirs on this job, a
  // phase-less Start would move a job they are not dated to work.
  const canStart =
    (job.status === 'pending' || job.status === 'scheduled') && startablePhases.length === 0 && mismatchedPhase === null;
  // Resume sends the phase that is running, when exactly one is — a COALESCE
  // no-op on its clock — and no phase otherwise. Never a guess at a new one.
  const canResume = job.status === 'on_hold';
  const resumePhase: FieldPhase | undefined = endablePhases.length === 1 ? endablePhases[0] : undefined;

  /**
   * The work-finished affordance, mirroring what the API accepts: active and
   * on_hold, and only while nobody has declared it yet. A button certain to
   * 409 is worse than no button — the same rule the three above follow.
   *
   * on_hold is included deliberately, matching the API: a hand who parked a
   * job at end of day and finished it next morning without pressing Resume
   * must still be able to record that.
   *
   * `workFinishedAt ?? work_finished_at` — the /employee/* surface is
   * snake_case and that is the convention there, not drift. The camelCase read
   * is first only so this keeps working if the payload is ever normalised;
   * today it is always the second. See CLAUDE.md on the two casing surfaces.
   */
  const workFinishedAt: string | null =
    (job as any).workFinishedAt ?? (job as any).work_finished_at ?? null;
  const canMarkWorkFinished =
    !workFinishedAt && (job.status === 'active' || job.status === 'on_hold');

  const statusMutating =
    startJobMutation.isPending ||
    endPhaseMutation.isPending ||
    resumePhaseMutation.isPending ||
    stopJobMutation.isPending ||
    workFinishedMutation.isPending ||
    undoWorkFinishedMutation.isPending;
  const showStatusCard =
    canStart || canResume || canHold || canMarkWorkFinished || !!workFinishedAt || !!statusError ||
    (mismatchedPhase !== null && !closed) || startablePhases.length > 0 || endablePhases.length > 0 || resumablePhases.length > 0;

  return (
    <div className="animate-fade-in min-h-full bg-fieldiq-dark pb-32">
      <div className="max-w-[1920px] mx-auto">
        <Header />
        <div className="px-4 py-3 border-b border-fieldiq-border flex items-center gap-3">
          <button onClick={goBack} className="text-fieldiq-text">
            <ArrowLeft size={26} />
          </button>
          <div>
            <h1 className="text-xl font-bold text-white">{job.job_number}</h1>
            <span className={`text-xs px-2 py-0.5 rounded-full ${jobStatusStyle(job.status)}`}>
              {jobStatusLabel(job.status)}
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-[1920px] mx-auto px-4 py-4 space-y-4">
        {/* ─── Job status actions ──────────────────────────────────────────
            Directly under the header rather than beside the badge: the header
            is the least reachable strip on a phone and already carries the back
            arrow, job number and badge. This sits inches lower, still reads as
            "about the status", and the buttons are full-width thumb targets.

            Bottom safe-area is already handled — FieldBottomNav renders an
            env(safe-area-inset-bottom) spacer and the page wrapper carries
            pb-32 to clear it. Nothing to add here.

            THERE IS STILL NO COMPLETE BUTTON, and the reasoning is unchanged:
            completing a job fires crew SMS, Slack and the reserved-equipment
            release, it stays an office action, and the API has no field
            endpoint for it. (The torque-sheet queue is no longer in that list —
            API #399 removed the completion insert, having measured that the
            create path already covers it.)

            WHAT CHANGED is that a hand is no longer stuck. "Mark Work Finished"
            below records that the work is done WITHOUT touching the status, and
            the office confirms by closing the job as it always has. That is a
            fact, not a state — which is the point: ~25 API sites filter
            jobs.status positively and a new member would be silently excluded
            by all of them, GET /employee/my-equipment included. A hand who
            marked the job finished would have lost the very screen they use to
            say where the tong went. */}
        {showStatusCard && (
          <div className="bg-fieldiq-card rounded-xl p-4 space-y-3">
            {/* One Start per phase — see `startablePhases` above for which
                appear. The first one on a pending job also moves it to active;
                on a held job it resumes it. Named for its phase because a tap
                starts ONE clock. */}
            {startablePhases.map((p) => (
              <button
                key={`start-${p}`}
                onClick={() => startJobMutation.mutate(p)}
                disabled={!isOnline || statusMutating}
                className="w-full bg-fieldiq-accent text-fieldiq-dark font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm active:scale-[0.98] transition-transform disabled:opacity-50 disabled:active:scale-100"
              >
                {startJobMutation.isPending && startJobMutation.variables === p
                  ? <Loader2 size={18} className="animate-spin" />
                  : <Play size={18} />}
                {p === 'wash' ? 'Start Wash' : 'Start Run'}
              </button>
            ))}

            {/* A single-phase hand on a job that has no such phase — say so
                rather than offer the other phase's button. */}
            {mismatchedPhase !== null && !closed && (
              <p className="text-fieldiq-text text-sm flex items-start gap-2" data-testid="phase-mismatch">
                <AlertTriangle size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
                {/* Only the wash can be missing — the run is always a phase. */}
                You're on the {PHASE_WORD[mismatchedPhase].toLowerCase()} crew, but this job has no{' '}
                {PHASE_WORD[mismatchedPhase].toLowerCase()} date. Ask dispatch.
              </p>
            )}

            {/* No phase left to name — see `canStart`. */}
            {canStart && (
              <button
                onClick={() => startJobMutation.mutate(undefined)}
                disabled={!isOnline || statusMutating}
                className="w-full bg-fieldiq-accent text-fieldiq-dark font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm active:scale-[0.98] transition-transform disabled:opacity-50 disabled:active:scale-100"
              >
                {startJobMutation.isPending && startJobMutation.variables === undefined
                  ? <Loader2 size={18} className="animate-spin" />
                  : <Play size={18} />}
                Start Job
              </button>
            )}

            {/* Same endpoint as Start — the API allows on_hold → active. Only
                the wording changes, because "Start" on a job the crew already
                started once reads as though it would wipe the clock. It does
                not: every clock is COALESCEd server-side and keeps the first
                start. Sends the running phase — see `resumePhase`. */}
            {canResume && (
              <button
                onClick={() => startJobMutation.mutate(resumePhase)}
                disabled={!isOnline || statusMutating}
                className="w-full bg-fieldiq-accent text-fieldiq-dark font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm active:scale-[0.98] transition-transform disabled:opacity-50 disabled:active:scale-100"
              >
                {startJobMutation.isPending && startJobMutation.variables === resumePhase
                  ? <Loader2 size={18} className="animate-spin" />
                  : <Play size={18} />}
                Resume
              </button>
            )}

            {/* One End per running phase. Records the clock only — the job's
                status does not change, and closing it stays an office action. */}
            {endablePhases.map((p) => (
              <button
                key={`end-${p}`}
                onClick={() => endPhaseMutation.mutate(p)}
                disabled={!isOnline || statusMutating}
                className="w-full bg-fieldiq-dark border border-fieldiq-border text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm active:scale-[0.98] transition-transform disabled:opacity-50 disabled:active:scale-100"
              >
                {endPhaseMutation.isPending && endPhaseMutation.variables === p
                  ? <Loader2 size={18} className="animate-spin" />
                  : <CircleStop size={18} />}
                {p === 'wash' ? 'End Wash' : 'End Run'}
              </button>
            ))}

            {resumablePhases.map((p) => (
              <button
                key={`resume-${p}`}
                onClick={() => resumePhaseMutation.mutate(p)}
                disabled={!isOnline || statusMutating}
                className="w-full bg-fieldiq-dark border border-fieldiq-border text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm active:scale-[0.98] transition-transform disabled:opacity-50 disabled:active:scale-100"
              >
                {resumePhaseMutation.isPending && resumePhaseMutation.variables === p
                  ? <Loader2 size={18} className="animate-spin" />
                  : <RotateCw size={18} />}
                {p === 'wash' ? 'Resume Wash' : 'Resume Run'}
              </button>
            ))}

            {canHold && (
              <button
                onClick={() => stopJobMutation.mutate()}
                disabled={!isOnline || statusMutating}
                className="w-full bg-orange-500/15 border border-orange-500/30 text-orange-400 font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm active:scale-[0.98] transition-transform disabled:opacity-50 disabled:active:scale-100"
              >
                {stopJobMutation.isPending
                  ? <Loader2 size={18} className="animate-spin" />
                  : <Pause size={18} />}
                Put On Hold
              </button>
            )}

            {/* Green rather than amber: amber is the primary "get moving"
                action on this screen (Start/Resume) and this is the opposite
                end of the job. It is also not destructive, so the orange
                treatment On Hold uses would overstate it. */}
            {canMarkWorkFinished && (
              <button
                onClick={() => workFinishedMutation.mutate()}
                disabled={!isOnline || statusMutating}
                className="w-full bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm active:scale-[0.98] transition-transform disabled:opacity-50 disabled:active:scale-100"
              >
                {workFinishedMutation.isPending
                  ? <Loader2 size={18} className="animate-spin" />
                  : <CheckCircle2 size={18} />}
                Mark Work Finished
              </button>
            )}

            {/* Once declared, the button is replaced by what it accomplished.
                A hand needs to know the office has been told and that the job
                is not yet closed — otherwise the card just goes blank and the
                tap reads as having done nothing. Says WHO, because on a
                multi-hand crew the answer to "has anyone called this in?" is
                the whole question. */}
            {workFinishedAt && (
              <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-3 flex items-start gap-2">
                <CheckCircle2 size={16} className="text-emerald-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-emerald-400 text-sm font-medium">
                    Work marked finished
                  </p>
                  <p className="text-fieldiq-muted text-xs mt-0.5">
                    The office has been told and will close the job. Your tickets are still editable.
                  </p>

                  {/* ON THE BANNER, not beside the declare button, and the
                      placement is the whole argument.

                      This does not exist for a stray thumb — the declaration is
                      already forgiving there, because a second tap is a no-op
                      that keeps the first hand's authorship. It exists for the
                      WRONG JOB: a hand on a two-job day taps the one at the top
                      of the list. That hand is looking at THIS banner, on a job
                      they have not finished, and the way out belongs where the
                      screen is saying the wrong thing.

                      Underplayed on purpose — a link, not a button. Putting a
                      second full-width control here would make "undo" look like
                      half the job's workflow rather than a correction, and it
                      would sit under the thumb that just pressed declare. */}
                  <button
                    onClick={() => undoWorkFinishedMutation.mutate()}
                    disabled={!isOnline || statusMutating}
                    className="mt-2 text-xs text-fieldiq-muted underline underline-offset-2 hover:text-white disabled:opacity-50 disabled:no-underline"
                  >
                    {undoWorkFinishedMutation.isPending
                      ? 'Undoing…'
                      : "Not this job? Undo"}
                  </button>
                </div>
              </div>
            )}

            {/* Fails loudly rather than queueing. There is no offline path for
                these two verbs, and a queued status change is the worst version
                of this feature: the phone says Active, the office board says
                Scheduled, and nobody finds out until someone asks where the
                crew is. Rendered inline, not as a title attribute — a tooltip
                does not exist on a touch device. */}
            {!isOnline && (
              <p className="text-fieldiq-muted text-xs flex items-start gap-2">
                <AlertTriangle size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
                You're offline — job status can't be changed until you reconnect.
              </p>
            )}

            {statusError && (
              <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
                <AlertTriangle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-red-400 text-sm flex-1">{statusError}</p>
                <button onClick={() => setStatusError(null)} className="text-red-400 hover:text-red-300">
                  <X size={14} />
                </button>
              </div>
            )}
          </div>
        )}

        {/* Job Info */}
        <div className="bg-fieldiq-card rounded-xl p-4 space-y-3">
          <h3 className="font-semibold text-white">Job Details</h3>
          {job.customer_name && (
            <div>
              <p className="text-xs text-fieldiq-muted">Client</p>
              <p className="text-white font-bold text-lg">{job.customer_name}</p>
              {(job.customer_contact_name || job.main_contact_name) && (
                <p className="text-fieldiq-text text-sm">Customer: {job.customer_contact_name || job.main_contact_name}</p>
              )}
            </div>
          )}
          <div className="grid grid-cols-2 gap-3 text-sm">
            {job.job_type && (
              <div>
                <span className="text-fieldiq-muted text-xs block">Job Type</span>
                <span className="text-white font-medium">{getJobTypeLabel(job.job_type)}</span>
              </div>
            )}
            {/*
              The date and on-location time of the phase(s) THIS HAND is shown
              — the same phases as the Start buttons above (`startOffered`), so
              a button never sits over another phase's date. A wash-only hand
              on TNT J-4404 saw "Date Nov 9" (the run) over Start Wash, with
              the wash on Nov 4. Each is labelled Wash or Run; a both/NULL hand
              with no row day sees both. A mismatched hand sees their own
              phase, em-dashed, matching the line above.

              Wall-clock columns, read as written — never through the tenant
              zone (calendar-date.ts). The em dash matters most here: this is
              where "what time am I on location?" gets asked.
            */}
            {(startOffered.length > 0 ? startOffered : mismatchedPhase ? [mismatchedPhase] : []).map((p) => {
              const value = phaseDateValue(job, p);
              return (
                <React.Fragment key={`date-${p}`}>
                  <div data-testid={`phase-date-${p}`}>
                    <span className="text-fieldiq-muted text-xs block">{PHASE_WORD[p]} Date</span>
                    <span className="text-white font-medium">{formatCalendarDate(value, { month: 'short', day: 'numeric', year: 'numeric' }) ?? '—'}</span>
                  </div>
                  <div data-testid={`phase-time-${p}`}>
                    <span className="text-fieldiq-muted text-xs block">{PHASE_WORD[p]} On Location</span>
                    <span className="text-white font-medium">{formatScheduledTime(value) ?? '—'}</span>
                  </div>
                </React.Fragment>
              );
            })}
            {(job.rig || job.rig_name) && (
              <div>
                <span className="text-fieldiq-muted text-xs block">Rig</span>
                <span className="text-white font-medium">{job.rig || job.rig_name}{job.rig_number ? ` (${job.rig_number})` : ''}</span>
              </div>
            )}
            {/*
              Well and LSD moved into WellInfoFillCard, which spans both grid
              columns because it carries the fill affordance below the two
              values. They used to render ONLY when populated, so a blank LSD
              was invisible on the one screen whose reader is standing on the
              lease and can supply it — there was nothing to press because there
              was nothing to see. They now always render, em dash when absent.
            */}
            <div className="col-span-2">
              <WellInfoFillCard
                jobId={jobId!}
                lsd={job.lsd ?? job.well_id_lsd}
                wellName={job.well ?? job.well_name}
                isOnline={isOnline}
                onFilled={invalidateJobStatus}
              />
            </div>
            {/*
              Top Drive and Depth moved up from the casing card. Both are
              JOB-level — `jobs.top_drive_thread` and `jobs.depth_metres`, not
              columns on `job_casing_strings`. With N casing cards, repeating
              them on each is noise and showing them on card 1 only reads as
              though they are scoped to string 1. Same labels, same formatting,
              so a hand recognises them in their new place.
            */}
            {job.top_drive_thread && (
              <div>
                <span className="text-fieldiq-muted text-xs block">Top Drive</span>
                <span className="text-white font-medium">{job.top_drive_thread}</span>
              </div>
            )}
            {job.depth_metres && (
              <div>
                <span className="text-fieldiq-muted text-xs block">Depth</span>
                <span className="text-white font-medium">{Number(job.depth_metres).toLocaleString()}m</span>
              </div>
            )}
          </div>
        </div>

        {/*
          One card PER STRING, in string_number order. Was one card built from
          the flat string-1 columns, with the heading "Casing Specs" whatever the
          job actually ran.

          Gate is `casingStrings.length > 0`. With Top Drive moved up to Job
          Details there is nothing else this card owns, so a job with no strings
          has no casing card at all rather than an empty shell.

          The heading suffix comes from `string_number`, NOT the array index: a
          job whose string 1 was deleted must still read "String 2". A single
          string gets `null` and the plain "Casing Specs" heading the screen has
          always shown.

          `key` is the child row id where there is one. `string_number` is the
          fallback's only identifier and is unique per job either way, but it is
          an ordinal step 7 will let the editor renumber — so it is the second
          choice, not the first.
        */}
        {casingStrings.map((s) => (
          <FieldCasingStringCard
            key={s.id ?? `string-${s.string_number}`}
            string={s}
            headingSuffix={casingStrings.length > 1 ? `String ${s.string_number}` : null}
            jobId={jobId!}
            isOnline={isOnline}
            onCorrected={invalidateJobStatus}
            // A hand may enter torque for a combo with nothing on file, never
            // change one (api#830). The card enforces "nothing on file".
            allowTorqueEntry
          />
        ))}

        {/* Directions & Instructions */}
        {(job.directions || job.instructions) && (
          <div className="bg-fieldiq-card rounded-xl p-4 space-y-3">
            {job.directions && (
              <div>
                <h4 className="text-xs text-fieldiq-muted uppercase mb-1">Directions</h4>
                <p className="text-sm whitespace-pre-wrap">{job.directions}</p>
              </div>
            )}
            {job.instructions && (
              <div>
                <h4 className="text-xs text-fieldiq-muted uppercase mb-1">Instructions</h4>
                <p className="text-sm whitespace-pre-wrap">{job.instructions}</p>
              </div>
            )}
          </div>
        )}

        {/* Crew */}
        {crew.length > 0 && (
          <div className="bg-fieldiq-card rounded-xl p-4">
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Users size={18} className="text-fieldiq-accent" /> Crew ({crew.length})
            </h3>
            <div className="space-y-2">
              {crew.map((member: any) => (
                <div key={member.id} className="flex items-center justify-between text-sm bg-fieldiq-dark rounded-lg px-3 py-2">
                  <span className="text-white font-medium">{member.name || [member.first_name, member.last_name].filter(Boolean).join(' ') || 'Unknown'}</span>
                  <span className="text-xs text-fieldiq-muted">{crewRoleLabel(member)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Equipment
            ─────────────────────────────────────────────────────────────────
            THE GUARD MOVED, AND THAT IS THE POINT OF THIS SECTION.

            This card used to render only when `equipment.length > 0`, which
            was fine while it was read-only. With an Add control on it, that
            guard hides the way in exactly when the crew needs it — a job with
            no equipment yet is precisely the job somebody is standing at the
            shop trying to load.

            So the card renders whenever it has something to offer: any
            equipment to show, OR the capability to add some. It disappears
            entirely only for a tenant with the flag off and a job with no gear,
            which is the pre-#364 world and correctly shows nothing. */}
        {(equipment.length > 0 || canAssignEquipment) && (
          <div className="bg-fieldiq-card rounded-xl p-4">
            <div className="flex items-center justify-between mb-3 gap-2">
              <h3 className="font-semibold flex items-center gap-2">
                <Wrench size={18} className="text-fieldiq-accent" /> Equipment ({equipment.length})
              </h3>
              {canAssignEquipment && (
                <button
                  onClick={() => navigate(`/field/jobs/${jobId}/assign-equipment`)}
                  className="flex items-center gap-1 min-h-[36px] px-3 rounded-lg bg-fieldiq-accent text-fieldiq-dark text-xs font-semibold"
                >
                  <Plus size={14} /> Add
                </button>
              )}
            </div>

            {equipment.length === 0 ? (
              <p className="text-sm text-fieldiq-muted">
                No equipment on this job yet.
              </p>
            ) : (
              <div className="space-y-2">
                {equipment.map((eq: any) => (
                  <div key={eq.id} className="flex items-center justify-between text-sm bg-fieldiq-dark rounded-lg px-3 py-2 gap-2">
                    <span className="min-w-0 truncate">{eq.name}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {eq.cert_upload_id && (
                        <Tooltip content="View Certificate">
                          <button
                            onClick={() => handleViewCert(eq.id)}
                            disabled={certLoading === eq.id}
                            className="text-green-400 hover:text-green-300 disabled:opacity-50"
                            aria-label="View Certificate"
                          >
                            {certLoading === eq.id ? <Loader2 size={14} className="animate-spin" /> : <Shield size={14} />}
                          </button>
                        </Tooltip>
                      )}
                      <span className="text-xs text-fieldiq-muted">{eq.unit_number}</span>
                      {/* Remove is offered on EVERY row, whoever put it there.
                          No check against assigned_source — see the mutation. */}
                      {canAssignEquipment && (
                        <Tooltip content="Remove from job">
                          <button
                            onClick={() => setRemoveTarget(eq)}
                            disabled={removeEquipment.isPending}
                            className="text-fieldiq-muted hover:text-red-400 disabled:opacity-50 p-1 -m-1"
                            aria-label={`Remove ${eq.name}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </Tooltip>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <ConfirmModal
          isOpen={removeTarget !== null}
          title="Remove equipment?"
          confirmLabel="Remove"
          loading={removeEquipment.isPending}
          onCancel={() => setRemoveTarget(null)}
          onConfirm={() => removeTarget && removeEquipment.mutate(removeTarget.id)}
          body={
            <p>
              Take <span className="font-semibold text-white">{removeTarget?.name}</span> off this job?
              It goes back to the available pool.
            </p>
          }
        />

        {/* Tasks */}
        {tasks.length > 0 && (() => {
          const completedCount = tasks.filter((t: any) => t.completed || t.is_completed || t.isCompleted).length;
          return (
            <div className="bg-fieldiq-card rounded-xl p-4">
              <h3 className="font-semibold mb-3 flex items-center gap-2">
                <ClipboardCheck size={18} className="text-fieldiq-accent" /> Tasks ({completedCount}/{tasks.length})
              </h3>
              {tasks.length > 0 && (
                <div className="h-2 bg-fieldiq-border rounded-full overflow-hidden mb-3">
                  <div
                    className="h-full bg-fieldiq-accent-signal rounded-full transition-all"
                    style={{ width: `${(completedCount / tasks.length) * 100}%` }}
                  />
                </div>
              )}
              <div className="space-y-2">
                {tasks.map((task: any) => {
                  const done = task.completed || task.is_completed || task.isCompleted;
                  return (
                    <button
                      key={task.id}
                      onClick={() => toggleTaskMutation.mutate({ taskId: task.id, isCompleted: !done })}
                      className="w-full flex items-center gap-3 text-sm bg-fieldiq-dark rounded-lg px-3 py-3 active:scale-[0.98] transition-transform text-left"
                    >
                      {done ? (
                        <CheckSquare size={20} className="text-green-400 flex-shrink-0" />
                      ) : (
                        <Square size={20} className="text-fieldiq-muted flex-shrink-0" />
                      )}
                      <span className={done ? 'line-through text-fieldiq-muted' : 'text-white'}>
                        {task.title || task.description}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })()}
        {/* Attachments */}
        <div className="bg-fieldiq-card rounded-xl p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold flex items-center gap-2">
              <Paperclip size={18} className="text-fieldiq-accent" />
              Attachments{attachments && attachments.length > 0 ? ` (${attachments.length})` : ''}
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowAddLink(true)}
                className="flex items-center gap-1.5 bg-fieldiq-accent/15 text-fieldiq-accent text-sm font-semibold px-3 py-2 rounded-lg active:scale-95 transition-transform"
              >
                <Link2 size={16} />
                Add Link
              </button>
              <input
                ref={attachmentInputRef}
                type="file"
                accept={ALLOWED_ATTACHMENT_TYPES}
                onChange={handleAttachmentSelect}
                className="hidden"
              />
              <button
                onClick={() => attachmentInputRef.current?.click()}
                disabled={uploadAttachmentMutation.isPending}
                className="flex items-center gap-1.5 bg-fieldiq-accent/15 text-fieldiq-accent text-sm font-semibold px-3 py-2 rounded-lg active:scale-95 transition-transform disabled:opacity-50"
              >
                <Upload size={16} />
                Upload
              </button>
            </div>
          </div>

          {/* Upload progress */}
          {uploadProgress !== null && (
            <div className="mb-3">
              <div className="h-2 bg-fieldiq-border rounded-full overflow-hidden">
                <div
                  className="h-full bg-fieldiq-accent-signal rounded-full transition-all"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              <p className="text-xs text-fieldiq-muted mt-1">Uploading… {uploadProgress}%</p>
            </div>
          )}

          {/* Validation / upload error */}
          {attachmentError && (
            <div className="mb-3 bg-red-500/10 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
              <AlertTriangle size={16} className="text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-red-400 text-sm flex-1">{attachmentError}</p>
              <button onClick={() => setAttachmentError(null)} className="text-red-400 hover:text-red-300">
                <X size={14} />
              </button>
            </div>
          )}

          {/* Attachment list */}
          {attachments && attachments.length > 0 ? (
            <div className="space-y-2">
              {attachments.map((att: any) => {
                const isLink = att.type === 'external_link' || att.attachment_type === 'external_link';
                const displayName = isLink
                  ? (att.label ?? att.original_filename ?? att.originalFilename)
                  : (att.original_filename ?? att.originalFilename);
                const href = isLink
                  ? (att.external_url ?? att.externalUrl ?? att.url)
                  : (att.download_url ?? att.downloadUrl);
                return (
                  <div key={att.id} className="flex items-center gap-3 bg-fieldiq-dark rounded-lg px-3 py-3">
                    <FileText size={18} className="text-fieldiq-muted flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <p className="text-white text-sm font-medium truncate">{displayName}</p>
                      <p className="text-fieldiq-muted text-xs">
                        {isLink ? 'Link' : formatFileSize(att.size_bytes)}
                        {(att.uploaded_by_name ?? att.uploadedByName) && ` · ${att.uploaded_by_name ?? att.uploadedByName}`}
                        {(att.created_at ?? att.createdAt) && ` · ${formatInstantDay(att.created_at ?? att.createdAt, timeZone, { month: 'short', day: 'numeric' }, 'en-CA') ?? '—'}`}
                      </p>
                    </div>
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1.5 bg-fieldiq-accent/15 text-fieldiq-accent text-sm font-semibold px-3 py-2 rounded-lg active:scale-95 transition-transform flex-shrink-0"
                    >
                      <Download size={16} />
                    </a>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-fieldiq-muted text-sm">No attachments</p>
          )}
        </div>
      </div>

      {/* ─── The job's own tickets ───────────────────────────────────────────
          Directly above the create control, not buried up the page. The whole
          failure this closes is a hand reaching the amber button without ever
          having been told a ticket already exists, so the list has to sit
          between the scroll and the thumb. */}
      <div className="px-4 mt-4">
        <div className="max-w-[1920px] mx-auto">
          <JobTicketsCard
            tickets={jobTickets}
            crew={crew}
            currentUserId={user?.id ?? null}
            isLoading={ticketsLoading}
            isOffline={!isOnline}
            onOpen={(ticketId) => navigate('/field/tickets', { state: { openTicketId: ticketId, tab: 'all' } })}
          />
        </div>
      </div>

      {/* Bottom Action Bar */}
      <div className="px-4 mt-4 mb-6">
        <div className="max-w-[1920px] mx-auto space-y-3">
          {/* ─── Create Ticket ──────────────────────────────────────────────
              WHAT THIS TAP ACTUALLY DOES, and why it needs a gate. It navigates
              to /field/tickets carrying `createFromJob`, and FieldTicketsScreen
              fires the create mutation from a MOUNT EFFECT — the ticket exists
              before the screen has painted. There is no confirm step over
              there and no check for an existing ticket, so on a job that
              already has one this is a one-tap duplicate.

              The gate is conditional on purpose. On a job with no ticket the
              obvious thing is the right thing and a modal would be friction
              taxing every correct tap. It appears only where the tap is
              ambiguous, which is exactly where a second, deliberate act is
              worth asking for. */}
          <button
            onClick={() => {
              if (jobTickets.length > 0) { setConfirmSecondTicket(true); return; }
              navigate('/field/tickets', { state: { createFromJob: job.id, jobNumber: job.job_number } });
            }}
            className="w-full bg-fieldiq-accent text-fieldiq-dark font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2 text-sm"
          >
            <Ticket size={18} /> {jobTickets.length > 0 ? 'Create Another Ticket' : 'Create Ticket'}
          </button>
          {/* Two columns on a phone, four across from sm. At four-across on a
              375px viewport each action gets ~78px, which wraps the labels and
              clips the icons — and Call is conditional, so the row is three
              wide as often as four. */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {job.customer_phone && (
              <a
                href={`tel:${job.customer_phone}`}
                className="bg-green-500/20 text-green-400 py-3 rounded-xl flex items-center justify-center gap-2 text-sm font-medium"
              >
                <Phone size={18} /> Call
              </a>
            )}
            {/* The forms library, not a form for this job: nothing on
                /forms/available is scoped to a job, and the field renderer
                takes no job id. Named plainly so it does not read as one. */}
            <button
              onClick={() => navigate('/field/forms')}
              className="bg-fieldiq-card border border-fieldiq-border text-fieldiq-text py-3 rounded-xl flex items-center justify-center gap-2 text-sm font-medium"
            >
              <FileText size={18} /> Forms
            </button>
            <button
              onClick={() => navigate('/field/equipment')}
              className="bg-fieldiq-card border border-fieldiq-border text-fieldiq-text py-3 rounded-xl flex items-center justify-center gap-2 text-sm font-medium"
            >
              <ClipboardCheck size={18} /> Equipment
            </button>
            {/* The job is in context here, so the receipt is filed against it
                without a picker. */}
            <button
              onClick={() => navigate(`/field/expenses/new?job=${job.id}`)}
              className="bg-fieldiq-card border border-fieldiq-border text-fieldiq-text py-3 rounded-xl flex items-center justify-center gap-2 text-sm font-medium"
            >
              <ReceiptText size={18} /> Receipt
            </button>
            <button
              onClick={() => setChatOpen(true)}
              className="bg-fieldiq-card border border-fieldiq-accent text-fieldiq-accent py-3 rounded-xl flex items-center justify-center gap-2 text-sm font-medium"
            >
              <MessageCircle size={18} /> Chat
            </button>
          </div>
        </div>
      </div>

      {/* ─── Add Link Modal ──────────────────────────────────────── */}
      {showAddLink && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70">
          <div className="bg-fieldiq-card rounded-2xl shadow-2xl w-full max-w-sm mx-4 p-6">
            <h3 className="text-lg font-bold text-white mb-4">Add Link</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-fieldiq-muted mb-1">Label</label>
                <input
                  type="text"
                  value={linkLabel}
                  onChange={(e) => setLinkLabel(e.target.value)}
                  placeholder="e.g. Torque Sheet"
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm placeholder:text-fieldiq-muted focus:outline-none focus:border-fieldiq-accent"
                />
              </div>
              <div>
                <label className="block text-sm text-fieldiq-muted mb-1">URL</label>
                <input
                  type="url"
                  value={linkUrl}
                  onChange={(e) => setLinkUrl(e.target.value)}
                  placeholder="https://drive.google.com/..."
                  className="w-full bg-fieldiq-dark border border-fieldiq-border rounded-lg px-3 py-2 text-white text-sm placeholder:text-fieldiq-muted focus:outline-none focus:border-fieldiq-accent"
                />
              </div>
              {linkError && (
                <p className="text-red-400 text-xs">{linkError}</p>
              )}
            </div>
            <div className="flex gap-3 mt-5">
              <button
                onClick={() => { setShowAddLink(false); setLinkLabel(''); setLinkUrl(''); setLinkError(null); }}
                className="flex-1 py-2.5 bg-fieldiq-dark text-white font-medium rounded-lg text-sm hover:text-white transition-colors"
                disabled={addLinkMutation.isPending}
              >
                Cancel
              </button>
              <button
                onClick={handleAddLink}
                disabled={addLinkMutation.isPending}
                className="flex-1 py-2.5 bg-fieldiq-accent text-black font-semibold rounded-lg text-sm hover:bg-amber-400 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {addLinkMutation.isPending && (
                  <Loader2 size={14} className="animate-spin" />
                )}
                {addLinkMutation.isPending ? 'Adding...' : 'Add Link'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={confirmSecondTicket}
        title="This job already has a ticket"
        confirmLabel="Create another anyway"
        confirmVariant="primary"
        onCancel={() => setConfirmSecondTicket(false)}
        onConfirm={() => {
          setConfirmSecondTicket(false);
          navigate('/field/tickets', { state: { createFromJob: job.id, jobNumber: job.job_number } });
        }}
        body={
          <>
            <p>
              {jobTickets.length === 1
                ? 'There is already a ticket on this job:'
                : `There are already ${jobTickets.length} tickets on this job:`}
            </p>
            <ul className="space-y-1">
              {jobTickets.map((t: any) => (
                <li key={t.id} className="text-white">
                  {t.ticket_number ?? t.ticketNumber ?? '—'}
                  {' · '}
                  {formatCalendarDate(t.work_date, { month: 'short', day: 'numeric' }) ?? '—'}
                </li>
              ))}
            </ul>
            {/* The reason a second ticket is usually wrong is a billing reason,
                so say the billing thing. "Are you sure?" tells a hand nothing
                they can weigh. */}
            <p>
              A second ticket bills the customer as a second visit. If this is
              another day on the same job, put it on the ticket above instead.
            </p>
          </>
        }
      />

      {/* Crew Chat Drawer */}
      {jobId && (
        <JobChatDrawer
          jobId={jobId}
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
          currentUserId={user?.id || ''}
          onNewMessage={(msg) => {
            const name = [msg.user?.firstName, msg.user?.lastName].filter(Boolean).join(' ') || 'Someone';
            setMessage({ type: 'success', text: `💬 ${name}: ${(msg.message || '').slice(0, 50)}` });
            setTimeout(() => setMessage(null), 5000);
          }}
        />
      )}
    </div>
  );
}
